/**
 * The journal, second edition: four figures, the risk-capital card that folds
 * away, the trades as a list that opens row by row with the form in place,
 * and a P&L calendar that filters the list by the period clicked.
 *
 * Shared by both layouts (`mobile` is the phone) and by both halves of the
 * journal: a backtest session uses the same list and form, without the
 * calendar or the risk card.
 *
 * Still outside the ledger: results stay in the currency each trade was
 * settled in. Where a złoty figure helps, it is an estimate at today's rate
 * and says so with "≈" -- the journal keeps no rate history of its own.
 */

import { h } from '../vdom.js';
import { C, MONO, css, tap, input, select, datePicker, planLimit, journalUsed, planLine } from './common.js';
import { pill } from './cashflow.js';
import { DANGER_TEXT, DANGER_BG, DANGER_LINE, GAIN_BG, GAIN_LINE } from './whatif.js';
import { tradingRunway } from '../../../shared/compute.js';
import { typedTwins } from '../../../shared/broker-trades.js';
import { localDay, numberText, TRADE_SIDES, TRADE_EXITS, TRADE_RISKS, SUGGESTED_CURRENCIES, parseNumber, toBase } from '../../../shared/model.js';

const TIME_FRAMES = ['M1', 'M5', 'M15', 'H1', 'H4', 'D1', 'W', 'M', 'Y'];
/* The view state as the store starts it, for a render that has none yet. */
const UI = {
  filter: 'all', range: null, openId: null, riskOpen: false, formOpen: false, editId: null, rev: 0,
  date: null, d: {}, calMode: 'day', calMonth: null
};
const uiOf = state => state.journalUi || UI;
const HEAT = {
  up: ['#1F302D', '#2B5446', '#7FF2CC'], upStrong: ['#1F4A3D', '#2B6B57', '#7FF2CC'],
  down: ['#3A2226', '#4A2E33', '#FF8A8A'], downStrong: ['#5A2F33', '#8A3A40', '#FFB0B0']
};
const pct = ratio => (Math.max(0, Math.min(1, ratio)) * 100).toFixed(1) + '%';
const mono = (text, style = {}) => h('span', { style: css({ fontFamily: MONO, ...style }) }, text);
const tone = value => (value > 0.005 ? C.accent : value < -0.005 ? DANGER_TEXT : C.textDim);
const box = (mobile, style, ...children) => h('div', {
  style: css({
    padding: mobile ? '14px' : '16px 18px 18px', border: `1px solid ${C.stroke}`, borderRadius: mobile ? '16px' : '14px',
    background: C.card, display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0, ...style
  })
}, ...children);
const today = () => localDay();
const pad = n => String(n).padStart(2, '0');
const ymd = d => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
const isoWeek = d => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  return Math.ceil(((t - new Date(Date.UTC(t.getUTCFullYear(), 0, 1))) / 864e5 + 1) / 7);
};
const onPlan = trade => trade.rulesFollowed !== false && trade.moneyManagement !== false;

/**
 * How the money of a set of trades is added up. One currency: as it is. Several:
 * in the base currency at today's rates, leaving out any that has no rate --
 * a dollar is never counted as a złoty.
 */
export function journalMoney(trades, state) {
  const metrics = state.metricsNow || state.metrics || null;
  const base = metrics?.baseCurrency || state.settings?.baseCurrency || 'PLN';
  const rates = metrics?.rates || {};
  const currencies = [...new Set(trades.map(row => row.currency || 'USD'))];
  const mixed = currencies.length > 1;
  const currency = mixed ? base : (currencies[0] || state.tradeCurrency || 'USD');
  const value = row => (mixed ? toBase(Number(row.netPl) || 0, row.currency, base, rates) : Number(row.netPl) || 0);
  /* A trade's result in the base currency, for the "≈" line under it. */
  const approx = (amount, cur) => (cur === base ? null : toBase(amount, cur, base, rates));
  return { mixed, currency, base, value, approx, currencies };
}

// -------------------------------------------------------------- figures ---

export function journalKpis(stats, trades, { t, fmt, state, mobile }) {
  const s = t.shell;
  const m = journalMoney(trades, state);
  const money = value => (value === null || value === undefined ? '—' : fmt.signedAmount(value, stats.currencies[0] || m.currency));
  const net = stats.mixed ? null : stats.netPl;
  const approx = net === null ? null : m.approx(net, stats.currencies[0] || m.currency);
  const n = stats.count;
  const positive = net !== null && net > 0.005;

  const tile = (label, value, sub, { color = C.text, tinted = false } = {}) => h('div', {
    style: css({
      padding: mobile ? '12px 13px' : '16px 18px', borderRadius: '14px', minWidth: 0,
      border: `1px solid ${tinted ? GAIN_LINE : C.stroke}`, background: tinted ? GAIN_BG : C.card,
      display: 'flex', flexDirection: 'column', gap: mobile ? '5px' : '7px'
    })
  },
    mono(label, { fontSize: mobile ? '10px' : '10.5px', letterSpacing: '1.2px', color: C.muted }),
    mono(value, { fontSize: mobile ? '18px' : '24px', fontWeight: 700, letterSpacing: mobile ? 0 : '-0.6px', color, whiteSpace: 'nowrap' }),
    h('div', { style: css({ fontSize: mobile ? '11.5px' : '12.5px', color: C.textDim, lineHeight: 1.4 }) }, sub));

  return h('div', { style: css({ display: 'grid', gap: '12px', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))' }) },
    tile(s.tlNet, money(net),
      stats.mixed ? s.jMixed(stats.currencies.join(', ')) : s.tlNetSub(n, approx === null ? null : fmt.amount(Math.round(approx), m.base)),
      { color: net === null ? C.faint : tone(net), tinted: positive }),
    tile(s.jWinRate, stats.winRate === null ? '—' : Math.round(stats.winRate * 100) + '%', s.tlWinSub(stats.wins, stats.losses)),
    tile(s.tlAvg, stats.mixed || !n ? '—' : money(stats.netPl / n),
      stats.mixed ? s.jMixed(stats.currencies.join(', ')) : stats.losses ? s.tlAvgSub(money(stats.avgWin), money(-Math.abs(stats.avgLoss))) : s.tlAvgNoLoss,
      { color: stats.mixed || !n ? C.faint : tone(stats.netPl) }),
    tile(s.jFollowRate, stats.followRate === null ? '—' : Math.round(stats.followRate * 100) + '%',
      stats.mixed ? '' : s.jPlanSplit(money(stats.plFollowed), money(stats.plBroken)),
      { color: stats.followRate === null ? C.faint : stats.followRate >= 0.999 ? C.accent : C.gold })
  );
}

// ---------------------------------------------------------- risk capital ---

/**
 * How much trading may use without touching the cushion (see `tradingRunway`),
 * folded to one line until it is opened. From today's figures, whichever month
 * is on screen: the money at risk is the money there is now.
 */
export function riskCard(trades, { state, t, fmt, actions, mobile }) {
  const s = t.shell;
  const ui = uiOf(state);
  const metrics = state.metricsNow || state.metrics;
  if (!metrics) return null;
  const r = tradingRunway(metrics, trades, {
    cushionMonths: state.cushionMonths, month: today().slice(0, 7), prop: state.propTrader, propAccounts: state.prop || []
  });
  const money = value => fmt.amount(Math.round(Math.abs(value)), r.currency);
  const prop = r.status === 'prop';
  const ok = r.status === 'ok' || prop;
  const unknown = r.status === 'unknown';
  const dot = unknown ? C.muted : ok ? C.accent : C.danger;
  const title = prop ? s.rcPropTitle : unknown ? s.rcTitle : ok ? s.rcOkTitle(money(r.riskCapital)) : r.status === 'warning' ? s.rcWarning(fmt.percent(r.lossShare)) : s.rcTitle;
  const sub = prop ? s.rcPropSub : unknown ? s.rcUnknown : ok ? s.rcOkSub : s.rcNone;
  const months = r.runwayMonths === null ? null : Math.floor(r.runwayMonths);

  const stat = (label, value, note, color = C.text) => h('div', { key: label, style: css({ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }) },
    mono(label.toUpperCase(), { fontSize: '10.5px', letterSpacing: '1.2px', color: C.muted }),
    mono(value, { fontSize: '18px', fontWeight: 700, color }),
    note && h('span', { key: 'n', style: css({ fontSize: '11.5px', color: C.muted }) }, note));
  const option = n => {
    const on = state.cushionMonths === n;
    return h('div', {
      key: 'm' + n, ...tap(() => actions.setCushionMonths(n), s.rcMonthsOption(n)), role: 'radio', 'aria-checked': on ? 'true' : 'false',
      style: css({
        height: '36px', display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: '8px', cursor: 'pointer',
        border: `1px solid ${on ? C.accent : C.strokeHi}`, background: on ? C.line : 'transparent', fontFamily: MONO, fontSize: '12px', color: on ? C.text : C.textDim
      })
    }, s.rcMonthsOption(n));
  };

  return h('div', {
    style: css({
      padding: '12px 16px', borderRadius: '14px', display: 'flex', flexDirection: 'column', gap: '14px',
      border: `1px solid ${unknown ? C.stroke : ok ? GAIN_LINE : DANGER_LINE}`, background: unknown ? C.card : ok ? GAIN_BG : DANGER_BG
    })
  },
    h('div', {
      ...tap(() => actions.setJournalUi({ riskOpen: !ui.riskOpen }), ui.riskOpen ? s.rcClose : s.rcOpen),
      'aria-expanded': ui.riskOpen ? 'true' : 'false',
      style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 18px', minHeight: '44px', cursor: 'pointer' })
    },
      h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 260px', minWidth: 0 }) },
        h('span', { style: css({ width: '9px', height: '9px', flex: '0 0 9px', borderRadius: '50%', background: dot }) }),
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
          h('span', { style: css({ fontSize: '14px', fontWeight: 700, color: unknown ? C.text : ok ? C.accentHi : DANGER_TEXT }) }, title),
          h('span', { style: css({ fontSize: '12px', color: C.textDim, lineHeight: 1.4 }) }, sub))),
      prop && h('span', {
        key: 'pm', style: css({ flex: '1 1 220px', fontFamily: MONO, fontSize: '11.5px', color: C.textDim })
      }, s.rcPropMonth(money(r.propSpent), money(r.propBack))),
      !unknown && !prop && h('div', { key: 'bar', style: css({ flex: '1 1 220px', minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '6px' }) },
        h('div', { style: css({ height: '6px', borderRadius: '3px', background: C.stroke, overflow: 'hidden' }) },
          h('div', { style: css({ width: pct(r.cushion ? r.savings / r.cushion : 0), height: '100%', borderRadius: '3px', background: dot }) })),
        mono(s.rcProgress(money(r.savings), money(r.cushion), r.cushionMonths), { fontSize: '11.5px', color: C.textDim })),
      mono(ui.riskOpen ? s.rcClose : s.rcOpen, { fontSize: '11px', fontWeight: 700, letterSpacing: '.6px', color: C.textDim, whiteSpace: 'nowrap' })),
    ui.riskOpen && h('div', { key: 'more', class: 'pt-rise', style: css({ display: 'flex', flexDirection: 'column', gap: '14px', paddingTop: '12px', borderTop: `1px solid ${C.stroke}` }) },
      prop && h('div', { key: 'pstats', style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px' }) },
        stat(s.rcSavings, money(r.savings), s.rcPropSavingsSub),
        stat(s.rcPropFees, r.propSpent ? '−' + money(r.propSpent) : money(0), s.rcPropFeesSub, r.propSpent ? DANGER_TEXT : C.text),
        stat(s.rcPropBack, r.propBack ? '+' + money(r.propBack) : money(0), s.rcPropBackSub, r.propBack ? C.accent : C.text),
        stat(s.rcTraded, fmt.signedAmount(r.tradedThisMonth, r.currency), s.rcPropTradedSub, tone(r.tradedThisMonth))),
      !unknown && !prop && h('div', { key: 'stats', style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px' }) },
        stat(s.rcSavings, money(r.savings), null),
        stat(s.rcCushion, money(r.cushion), s.rcCushionSub(r.cushionMonths)),
        stat(s.rcRunway, months === null ? '—' : s.rcRunwayValue(months), s.rcRunwaySub(money(r.monthlyOutgoings))),
        stat(s.rcTraded, fmt.signedAmount(r.tradedThisMonth, r.currency), r.lossShare === null ? null : s.rcTradedSub(fmt.percent(r.lossShare)), tone(r.tradedThisMonth))),
      /* Prop firm accounts put the firm's capital at risk, not the savings: then no cushion is kept. */
      h('div', {
        key: 'prop', ...tap(() => actions.setPropTrader(!state.propTrader), s.rcPropCheck), role: 'checkbox',
        'aria-checked': state.propTrader ? 'true' : 'false',
        style: css({ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer', minHeight: '36px', padding: '2px 0' })
      },
        h('div', {
          style: css({
            width: '18px', height: '18px', flex: '0 0 18px', marginTop: '1px', borderRadius: '5px',
            border: `1px solid ${state.propTrader ? C.accent : C.strokeHi}`, background: state.propTrader ? C.accent : 'transparent',
            color: C.bg, fontFamily: MONO, fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center'
          })
        }, state.propTrader ? '✓' : ''),
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
          h('span', { style: css({ fontSize: '12.5px', fontWeight: 600, color: C.text }) }, s.rcPropCheck),
          h('span', { style: css({ fontSize: '11.5px', color: C.muted, lineHeight: 1.45 }) }, s.rcPropCheckSub))),
      !prop && h('div', { key: 'cushion', role: 'radiogroup', style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }) },
        h('span', { style: css({ fontSize: '12.5px', color: C.textDim, marginRight: '4px' }) }, s.rcMonths),
        option(3), option(6), option(12)),
      r.unconverted.length > 0 && h('div', { key: 'unc', style: css({ fontSize: '11.5px', color: C.muted }) }, s.rcUnconverted(r.unconverted.join(', '))),
      h('div', { key: 'note', style: css({ fontSize: '12px', lineHeight: 1.5, color: C.muted }) }, prop ? s.rcPropNote : s.rcNote))
  );
}

// ---------------------------------------------------------------- list ---

const FILTERS = [
  ['all', () => true],
  ['LONG', row => row.side === 'LONG'],
  ['SHORT', row => row.side === 'SHORT'],
  ['off', row => !onPlan(row)]
];

export function tradeList(trades, ctx, { backtest = false, session = null } = {}) {
  const { state, t, fmt, actions, mobile } = ctx;
  const s = t.shell;
  const ui = uiOf(state);
  const m = journalMoney(trades, state);
  const all = trades.slice().sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const filter = FILTERS.find(([key]) => key === ui.filter) || FILTERS[0];
  const range = backtest ? null : ui.range;
  const shown = all.filter(filter[1]).filter(row => !range || (row.date >= range.from && row.date <= range.to));
  const label = key => (key === 'all' ? s.tlAll : key === 'off' ? s.tlOff : key === 'LONG' ? s.tlLong : s.tlShort);

  const chip = ([key, test]) => {
    const on = filter[0] === key;
    return h('div', {
      key, ...tap(() => actions.setJournalUi({ filter: key, openId: null }), label(key)), role: 'radio', 'aria-checked': on ? 'true' : 'false',
      style: css({
        flex: '0 0 auto', height: '36px', display: 'flex', alignItems: 'center', gap: '7px', padding: '0 12px', borderRadius: '9px', cursor: 'pointer', whiteSpace: 'nowrap',
        border: `1px solid ${on ? C.strokeHi : C.stroke}`, background: on ? C.line : 'transparent', fontSize: '12.5px', color: on ? C.text : C.textDim
      })
    }, label(key), mono(String(all.filter(test).length), { fontSize: '11px', color: C.muted }));
  };

  const startEdit = row => {
    actions.setJournalUi({
      formOpen: true, editId: row.id, rev: ui.rev + 1, date: row.date,
      d: {
        side: row.side || 'LONG', currency: row.currency || state.tradeCurrency, tf: row.timeFrame || null,
        exit: row.exitLogic || null, risk: row.risk || null, rules: row.rulesFollowed !== false, mm: row.moneyManagement !== false,
        propAccountId: row.propAccountId || null
      }
    });
    if (typeof requestAnimationFrame === 'function' && typeof document !== 'undefined') {
      requestAnimationFrame(() => document.getElementById('trade-form')?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
  };

  const line = row => {
    const open = ui.openId === row.id;
    const [y, mo, d] = String(row.date).split('-');
    const result = Number(row.netPl) || 0;
    const approx = m.approx(result, row.currency);
    const breaks = [row.rulesFollowed === false && s.tlBreakRules, row.moneyManagement === false && s.tlBreakMm].filter(Boolean);
    const check = (text, value, mark, color) => h('span', {
      key: text,
      style: css({
        display: 'flex', alignItems: 'center', gap: '6px', height: '30px', padding: '0 10px', borderRadius: '7px',
        border: `1px solid ${C.stroke}`, background: C.panel, fontSize: '12px', color: C.textDim
      })
    }, mark && h('span', { key: 'm', style: css({ color, fontWeight: 700 }) }, mark), text, value && mono(value, { key: 'v', fontSize: '11.5px', color: C.text }));
    const yes = value => (value === false ? ['✕', DANGER_TEXT] : ['✓', C.accent]);
    const rules = yes(row.rulesFollowed);
    const mm = yes(row.moneyManagement);

    return h('div', {
      key: row.id,
      style: css({ display: 'flex', flexDirection: 'column', borderBottom: `1px solid ${C.line}`, background: open || ui.editId === row.id ? C.line : 'transparent' })
    },
      h('div', {
        ...tap(() => actions.setJournalUi({ openId: open ? null : row.id }), row.pair), 'aria-expanded': open ? 'true' : 'false', class: 'pt-nav',
        style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: mobile ? '8px 12px' : '8px 18px', padding: '12px 6px', minHeight: '60px', cursor: 'pointer' })
      },
        h('div', { style: css({ flex: '0 0 50px', display: 'flex', flexDirection: 'column', gap: '2px' }) },
          mono(String(Number(d)), { fontSize: '15px', fontWeight: 700 }),
          mono((t.months[Number(mo) - 1] || '').toUpperCase() + ' ' + String(y).slice(2), { fontSize: '10.5px', letterSpacing: '1px', color: C.muted })),
        h('div', { style: css({ flex: '1 1 180px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }) },
          h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }) },
            mono(row.pair, { fontSize: '14.5px', fontWeight: 700 }),
            mono(row.side, {
              padding: '2px 7px', borderRadius: '5px', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.5px',
              background: row.side === 'SHORT' ? DANGER_BG : GAIN_BG, color: row.side === 'SHORT' ? DANGER_TEXT : C.accentHi
            }),
            row.lots ? mono(s.tlLots(fmt.number(Number(row.lots), 2)), { key: 'l', fontSize: '12px', color: C.muted }) : null),
          h('span', { style: css({ fontSize: '12.5px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) },
            [row.session, row.timeFrame, row.setup].filter(Boolean).join(' · ') || s.tlNoCtx)),
        !mobile && h('span', {
          key: 'plan',
          style: css({
            flex: '0 0 auto', display: 'flex', alignItems: 'center', height: '26px', padding: '0 9px', borderRadius: '7px', fontSize: '11.5px', fontWeight: 600, whiteSpace: 'nowrap',
            background: breaks.length ? 'rgba(227,194,122,.12)' : GAIN_BG, color: breaks.length ? C.gold : C.accentHi
          })
        }, breaks.length ? s.tlOffPlan(breaks.join(', ')) : s.tlOnPlan),
        h('div', { style: css({ flex: '0 0 auto', minWidth: '90px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', marginLeft: mobile ? 'auto' : 0 }) },
          mono(fmt.signedAmount(result, row.currency), { fontSize: '16px', fontWeight: 700, color: tone(result), whiteSpace: 'nowrap' }),
          approx !== null && mono('≈ ' + fmt.signedAmount(Math.round(approx), m.base), { key: 'a', fontSize: '11px', color: C.muted, whiteSpace: 'nowrap' })),
        mono(open ? '▴' : '▾', { flex: '0 0 12px', fontSize: '11px', color: C.faint })),
      open && h('div', {
        key: 'more', class: 'pt-rise',
        style: css({ display: 'flex', flexDirection: 'column', gap: '12px', padding: mobile ? '4px 6px 14px' : '4px 6px 14px 74px' })
      },
        mobile && h('span', {
          key: 'plan', style: css({ alignSelf: 'flex-start', fontSize: '12px', fontWeight: 600, color: breaks.length ? C.gold : C.accentHi })
        }, breaks.length ? s.tlOffPlan(breaks.join(', ')) : s.tlOnPlan),
        row.note && h('div', { key: 'note', style: css({ fontSize: '12.5px', color: C.textDim, lineHeight: 1.5, overflowWrap: 'anywhere' }) }, row.note),
        h('div', { style: css({ display: 'flex', flexWrap: 'wrap', gap: '6px' }) },
          check(s.tlExitL, row.exitLogic ? (s.tlExits[row.exitLogic] || row.exitLogic) : '—', '·', C.muted),
          check(s.tlRiskL, row.risk ? (s.tlRisks[row.risk] || row.risk) : '—', '·', C.muted),
          check(s.tlRulesL, '', rules[0], rules[1]),
          check(s.tlMmL, '', mm[0], mm[1])),
        h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }) },
          row.reportUrl && /^https?:\/\//i.test(row.reportUrl) && h('a', {
            key: 'report', href: row.reportUrl, target: '_blank', rel: 'noopener noreferrer',
            style: css({
              height: '36px', display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: '8px', textDecoration: 'none',
              border: `1px solid ${GAIN_LINE}`, background: GAIN_BG, color: C.accent, fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, letterSpacing: '.5px'
            })
          }, s.tlReport),
          h('div', { style: css({ flex: 1 }) }),
          pill(s.tlEdit, () => startEdit(row), { key: 'edit', title: s.editTradeTitle }),
          h('div', {
            key: 'del', ...tap(() => actions.removeTrade(row.id), s.removeLabel), class: 'pt-remove', title: s.removeLabel,
            style: css({ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', color: C.faint, fontSize: '16px', cursor: 'pointer' })
          }, '×')))
    );
  };

  const formOpen = ui.formOpen || ui.editId;
  return box(mobile, {},
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px 14px', flexWrap: 'wrap', paddingBottom: '4px' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '3px', flex: '1 1 200px', minWidth: 0 }) },
        h('div', { style: css({ fontSize: mobile ? '14px' : '15px', fontWeight: 700 }) }, backtest ? s.tlListBacktest : s.tlList),
        h('div', { style: css({ fontSize: '12.5px', color: C.muted }) }, s.tlListSub)),
      range && h('div', {
        key: 'range', ...tap(() => actions.setJournalUi({ range: null }), s.tlRangeClear), title: s.tlRangeClear, class: 'pt-button',
        style: css({
          flex: '0 0 auto', height: '36px', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 10px 0 12px', borderRadius: '9px', cursor: 'pointer',
          border: `1px solid ${C.accent}`, background: GAIN_BG, fontSize: '12.5px', whiteSpace: 'nowrap'
        })
      }, s.tlRange(range.label), h('span', { style: css({ color: C.accentHi, fontSize: '15px' }) }, '×')),
      h('div', { role: 'radiogroup', style: css({ display: 'flex', gap: '6px', overflowX: 'auto', maxWidth: '100%' }) }, ...FILTERS.map(chip))),
    state.tradesFailed
      ? h('div', { key: 'failed', style: css({ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', padding: '18px 6px', textAlign: 'center' }) },
        h('div', { style: css({ fontSize: '12.5px', color: C.muted, lineHeight: 1.5 }) }, s.jFailed),
        pill(s.jRetry, actions.retryTrades, { key: 'retry' }))
      : all.length === 0
        ? h('div', { key: 'empty', style: css({ padding: '18px 6px', fontSize: '12.5px', color: C.muted, textAlign: 'center', lineHeight: 1.5 }) },
          state.tradesLoading ? s.jLoading : backtest ? s.btEmpty : s.jEmpty)
        : h('div', { key: 'list', style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.ghost}` }) },
          ...shown.map(line),
          shown.length === 0 && h('div', { key: 'none', style: css({ padding: '22px 6px', fontSize: '13px', color: C.muted, textAlign: 'center' }) }, s.tlNoMatch)),
    formOpen
      ? tradeForm(all, ctx, { backtest, session })
      : h('div', {
        key: 'open', ...tap(() => openForm(all, ctx), s.tlNew), class: 'pt-button',
        style: css({
          marginTop: '6px', minHeight: '60px', padding: '12px 16px', border: `1px dashed ${C.strokeHi}`, borderRadius: '12px', cursor: 'pointer',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', textAlign: 'center'
        })
      },
        mono(s.tlNew, { fontSize: '12.5px', fontWeight: 700, letterSpacing: '1.4px', color: C.accent }),
        h('span', { style: css({ fontSize: '12.5px', color: C.muted }) }, s.tlNewHint)),
    /* A broker's export: the live journal only -- a backtest is replayed on charts, not traded on an account. */
    !backtest && !formOpen && h('div', { key: 'import' }, tradeImport(ctx))
  );
}

/**
 * Trades from Tradovate's Performance export: a button that opens the file,
 * then what was found in it -- nothing is written until it is confirmed -- and
 * then what the import did.
 */
function tradeImport(ctx) {
  const { state, t, fmt, actions, inputs, mobile } = ctx;
  const s = t.shell;
  const draft = state.tradeImport;

  if (!draft) {
    return h('label', {
      class: 'pt-button', role: 'button', tabindex: '0', title: s.tiHint,
      onkeydown: event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.currentTarget.querySelector('input[type=file]')?.click();
      },
      style: css({
        marginTop: '8px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '4px 10px',
        padding: '8px 14px', borderRadius: '10px', cursor: 'pointer', border: `1px solid ${C.stroke}`, textAlign: 'center'
      })
    },
      mono(s.tiButton, { fontSize: '11.5px', fontWeight: 700, letterSpacing: '1px', color: C.textDim }),
      h('span', { style: css({ fontSize: '12px', color: C.muted }) }, s.tiHint),
      h('input', {
        type: 'file', accept: '.csv,text/csv,text/plain', style: css({ display: 'none' }),
        onchange: event => {
          const file = event.target.files?.[0];
          event.target.value = '';
          actions.openTradeImport(file);
        }
      }));
  }

  const day = iso => {
    const [y, mo, d] = String(iso).split('-');
    return Number(d) + ' ' + (t.months[Number(mo) - 1] || '') + ' ' + y;
  };
  const panel = (...children) => h('div', {
    class: 'pt-rise',
    style: css({
      marginTop: '8px', padding: mobile ? '14px' : '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px',
      border: `1px solid ${C.strokeHi}`, borderRadius: '12px', background: C.panel
    })
  }, ...children);
  const head = mono('TRADOVATE · ' + draft.name, { fontSize: '10.5px', fontWeight: 700, letterSpacing: '1px', color: C.accent, overflowWrap: 'anywhere' });
  const quiet = (text, key) => h('div', { key, style: css({ fontSize: '12px', color: C.muted, lineHeight: 1.5 }) }, text);

  if (draft.result) {
    const r = draft.result;
    return panel(head,
      /* "Nothing new" only when nothing changed at all: a trade brought up to date is the news then. */
      h('div', { style: css({ fontSize: '13.5px', lineHeight: 1.5 }) }, r.added === 0 && r.updated > 0 ? s.tiUpdated(r.updated) : s.tiDone(r.added)),
      ((r.added > 0 && r.updated > 0) || r.typed > 0 || r.duplicates > 0 || r.overLimit > 0) && quiet([
        r.added > 0 && r.updated > 0 && s.tiUpdated(r.updated),
        r.typed > 0 && s.tiTypedSkipped(r.typed),
        r.duplicates > 0 && s.tiDuplicates(r.duplicates),
        r.overLimit > 0 && s.tiOverLimit(r.overLimit)
      ].filter(Boolean).join(' '), 'more'),
      h('div', { style: css({ display: 'flex' }) }, pill(s.tiClose, actions.closeTradeImport, { key: 'close' })));
  }

  const rows = draft.trades;
  const total = rows.reduce((sum, row) => sum + row.netPl, 0);
  const wins = rows.filter(row => row.netPl > 0).length;
  const losses = rows.filter(row => row.netPl < 0).length;
  const limit = planLimit(state, 'journalPerMonth');
  const room = limit === null ? null : Math.max(0, limit - journalUsed(state));
  const accounts = state.prop || [];
  /* Trades that look like ones typed in by hand: listed, and left out unless asked for. */
  const twins = typedTwins(rows, state.trades || []);
  const typedRows = rows.filter(row => twins.has(row.importKey));
  const sending = draft.keepTyped ? rows.length : rows.length - typedRows.length;
  const twinLine = row => {
    const near = twins.get(row.importKey);
    return h('div', { key: row.importKey, style: css({ display: 'flex', gap: '8px', flexWrap: 'wrap', fontFamily: MONO, fontSize: '11.5px', color: C.textDim }) },
      day(row.date) + ' · ' + row.pair + ' ' + row.side + ' · ' + fmt.signedAmount(row.netPl, 'USD'),
      h('span', { style: css({ color: C.faint }) }, s.tiTypedAs(near.pair, fmt.signedAmount(Number(near.netPl), near.currency || 'USD'))));
  };

  return panel(head,
    h('div', { style: css({ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px 14px' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '3px' }) },
        h('div', { style: css({ fontSize: '15px', fontWeight: 700 }) }, s.tiFound(rows.length)),
        quiet(day(rows[0].date) + ' – ' + day(rows[rows.length - 1].date) + ' · ' + s.tiWinLoss(wins, losses), 'span')),
      mono(fmt.signedAmount(total, 'USD'), { fontSize: '18px', fontWeight: 700, color: tone(total) })),
    accounts.length > 0 && h('div', { key: 'acct', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', maxWidth: mobile ? 'none' : '320px' }) },
      h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, s.jAccount),
      select(inputs, 'tiAccount', [{ value: '', label: s.jOwnAccount }, ...accounts.map(acc => ({
        value: acc.id, label: [acc.firm, acc.program].filter(Boolean).join(' ')
      }))], { height: '44px', border: `1px solid ${C.strokeHi}`, borderRadius: '10px', background: C.panel, padding: '0 10px', fontSize: '13px', width: '100%' }, {
        value: draft.propAccountId || '', ariaLabel: s.jAccount,
        onChange: value => actions.setTradeImport({ propAccountId: value || null })
      })),
    typedRows.length > 0 && h('div', {
      key: 'typed',
      style: css({ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(227,194,122,.35)', background: 'rgba(227,194,122,.08)' })
    },
      h('div', { style: css({ fontSize: '12.5px', lineHeight: 1.5, color: C.text }) }, s.tiTyped(typedRows.length)),
      ...typedRows.slice(0, 8).map(twinLine),
      typedRows.length > 8 && quiet('+' + (typedRows.length - 8), 'more'),
      h('div', {
        ...tap(() => actions.setTradeImport({ keepTyped: !draft.keepTyped }), s.tiKeepTyped), role: 'checkbox', 'aria-checked': draft.keepTyped ? 'true' : 'false',
        style: css({ display: 'flex', alignItems: 'center', gap: '10px', minHeight: '36px', cursor: 'pointer', fontSize: '12.5px', color: C.textDim })
      },
        h('span', {
          style: css({
            flex: '0 0 18px', height: '18px', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700,
            border: `1px solid ${draft.keepTyped ? C.accent : C.strokeHi}`, background: draft.keepTyped ? C.accent : 'transparent', color: C.bg || '#070C12'
          })
        }, draft.keepTyped ? '✓' : ''),
        s.tiKeepTyped)),
    quiet(s.tiAbout, 'about'),
    draft.skipped.length > 0 && quiet(s.tiSkipped(draft.skipped.slice(0, 12).join(', ') + (draft.skipped.length > 12 ? '…' : '')), 'skipped'),
    room !== null && sending > room && planLine(room === 0 ? s.tiNoRoom : s.tiRoom(room), { spent: room === 0, key: 'plan' }),
    h('div', { style: css({ display: 'flex', gap: '8px', flexWrap: 'wrap' }) },
      room === 0
        ? pill(s.planSeePlans, () => actions.openSettings('plan'), { primary: true, key: 'plans' })
        : pill(s.tiImport(room === null ? sending : Math.min(sending, room)), actions.confirmTradeImport, { primary: true, key: 'go' }),
      pill(s.tiCancel, actions.closeTradeImport, { muted: true, key: 'cancel' })));
}

/* A new trade starts from the last one's habits -- currency and interval -- and never from its answers about exit, risk or discipline. */
function openForm(all, { state, actions }) {
  const last = all[0] || {};
  actions.setJournalUi({
    formOpen: true, editId: null, rev: uiOf(state).rev + 1, date: today(),
    d: { side: 'LONG', currency: last.currency || state.tradeCurrency || 'USD', tf: last.timeFrame || null, exit: null, risk: null, rules: null, mm: null }
  });
}

// ---------------------------------------------------------------- form ---

/**
 * A trade in three steps: what it was, the circumstances (optional), and the
 * four questions about discipline. Exit and risk start unchosen, and the two
 * honesty questions must be answered: a journal that answers them for you is a
 * record of what you wish had happened.
 */
function tradeForm(all, ctx, { backtest, session }) {
  const { state, t, fmt, inputs, actions, mobile } = ctx;
  const s = t.shell;
  const ui = uiOf(state);
  const d = ui.d || {};
  const editing = ui.editId ? all.find(row => row.id === ui.editId) : null;
  const close = () => actions.setJournalUi({ formOpen: false, editId: null, rev: ui.rev + 1 });
  const setD = patch => actions.setJournalUi({ d: { ...d, ...patch } });
  const limit = backtest || editing ? null : planLimit(state, 'journalPerMonth');
  const used = journalUsed(state);
  const spent = limit !== null && used >= limit;
  const answered = d.rules !== null && d.rules !== undefined && d.mm !== null && d.mm !== undefined;

  const save = () => {
    if (spent) return actions.showError('api:plan_journal');
    const pair = inputs.value('tlPair').trim().toUpperCase();
    if (!pair) return actions.showError(s.pairNeeded);
    const netPl = parseNumber(inputs.value('tlPl'));
    if (netPl === null) return actions.showError(s.tlResultNeeded);
    if (!answered) return actions.showError(s.tlDisciplineNeeded);
    const trade = {
      date: ui.date || today(),
      pair,
      side: d.side || 'LONG',
      lots: parseNumber(inputs.value('tlLots')) || 0,
      netPl,
      currency: d.currency || state.tradeCurrency || 'USD',
      session: inputs.value('tlSession').trim(),
      timeFrame: d.tf || '',
      setup: inputs.value('tlSetup').trim(),
      reportUrl: inputs.value('tlReport').trim(),
      exitLogic: d.exit || '',
      risk: d.risk || '',
      rulesFollowed: d.rules === true,
      moneyManagement: d.mm === true,
      ...(backtest ? {} : { propAccountId: d.propAccountId || null })
    };
    if (editing) actions.saveTradeEdit(editing.id, trade);
    else actions.addTrade({ ...trade, ...(backtest && session ? { backtestId: session.id } : {}) });
    inputs.clear(['tlPair', 'tlLots', 'tlPl', 'tlSession', 'tlSetup', 'tlReport']);
    close();
  };

  const label = text => h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' }) }, text);
  const frame = { height: '44px', border: `1px solid ${C.strokeHi}`, borderRadius: '10px', background: C.panel };
  const field = (name, text, value, { placeholder = '', number = false, upper = false, wide = false } = {}) => h('label', {
    key: name, style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0, ...(wide ? { gridColumn: '1 / -1' } : {}) })
  },
    label(text),
    input(inputs, name, placeholder, {
      type: number ? 'number' : 'text', value, onEnter: save,
      style: { ...frame, padding: '0 12px', fontFamily: number || upper ? MONO : 'inherit', fontSize: number || upper ? '14px' : '13.5px', fontWeight: upper ? 700 : 400, textTransform: upper ? 'uppercase' : 'none' }
    }));
  /* A row of choices of which one may be picked; picking it again unpicks it when `clear` allows. */
  const seg = (key, options, { clear = false, colors = {}, wide = false } = {}) => h('div', {
    role: 'radiogroup', style: css({ display: 'flex', flexWrap: 'wrap', gap: '3px', padding: '3px', borderRadius: '9px', background: C.bg, ...(wide ? { flex: 1 } : {}) })
  }, ...options.map(([value, text]) => {
    const on = d[key] === value;
    return h('div', {
      key: String(value), ...tap(() => setD({ [key]: on && clear ? null : value }), text), role: 'radio', 'aria-checked': on ? 'true' : 'false',
      style: css({
        flex: wide ? 1 : '0 0 auto', height: '36px', minWidth: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 10px', borderRadius: '7px', cursor: 'pointer', whiteSpace: 'nowrap',
        background: on ? (colors[String(value)] || C.strokeHi) : 'transparent', color: on ? C.text : C.muted, fontFamily: MONO, fontSize: '11.5px', fontWeight: 700
      })
    }, text);
  }));
  const step = (n, text, note) => h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px' }) },
    mono(String(n), { width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: C.line, fontSize: '10.5px', fontWeight: 700, color: C.accent }),
    h('span', { style: css({ fontSize: '13.5px', fontWeight: 700 }) }, text),
    note && h('span', { key: 'n', style: css({ fontSize: '12px', color: C.faint }) }, note));
  const question = (text, control) => h('div', {
    key: text,
    style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${C.stroke}`, background: C.panel })
  }, h('span', { style: css({ flex: '1 1 120px', fontSize: '13px', color: C.textDim }) }, text), control);

  const base = (state.metricsNow || state.metrics)?.baseCurrency || 'PLN';
  const quick = [...new Set([d.currency || state.tradeCurrency || 'USD', 'USD', base])].slice(0, 3);
  const others = SUGGESTED_CURRENCIES.filter(code => !quick.includes(code));
  const recent = [...new Set(all.map(row => row.pair).filter(Boolean))].slice(0, 5);
  const tfs = d.tf && !TIME_FRAMES.includes(d.tf) ? [...TIME_FRAMES, d.tf] : TIME_FRAMES;
  const grid = (min, ...children) => h('div', { style: css({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${min}px), 1fr))`, gap: '10px' }) }, ...children);
  const verdict = !answered ? [s.tlVerdictAsk, C.muted] : d.rules && d.mm ? [s.tlVerdictOn, C.accentHi] : [s.tlVerdictOff, C.gold];
  const money = value => (value === null || value === undefined ? undefined : numberText(value));

  return h('div', {
    key: 'tradeform-' + (editing ? editing.id : 'new') + '-' + ui.rev, id: 'trade-form', class: 'pt-rise',
    style: css({
      marginTop: '6px', padding: mobile ? '14px' : '16px', borderRadius: '12px', background: C.panel,
      border: `1px solid ${editing ? C.goldDeep : C.stroke}`, display: 'flex', flexDirection: 'column', gap: '18px'
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px' }) },
      mono(editing ? s.tlFormEdit : s.tlFormNew, { fontSize: '10.5px', letterSpacing: '1.2px', color: editing ? C.gold : C.muted }),
      h('div', { style: css({ flex: 1 }) }),
      h('div', {
        ...tap(close, s.cfClose), class: 'pt-button', title: s.cfClose,
        style: css({ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', color: C.muted, fontSize: '16px', cursor: 'pointer' })
      }, '×')),

    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '10px' }) },
      step(1, s.tlStep1),
      grid(150,
        h('div', { key: 'date', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
          label(backtest ? s.btCandleDate || s.jDate : s.tlDateL),
          datePicker({
            id: 'tl-date', value: ui.date || today(), onPick: stamp => stamp && actions.setJournalUi({ date: stamp }),
            label: s.tlDateL, state, actions, t, fmt, mobile,
            triggerStyle: { ...frame, display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '13px', color: C.text, letterSpacing: '.6px' }
          })),
        field('tlPair', s.tlPairL, editing ? editing.pair : undefined, { placeholder: s.jPairHint, upper: true }),
        h('div', { key: 'side', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
          label(s.tlSideL),
          seg('side', TRADE_SIDES.map(side => [side, side]), { colors: { LONG: '#1F4A3D', SHORT: '#5A2F33' }, wide: true })),
        field('tlLots', s.tlLotsL, editing ? money(editing.lots) : undefined, { placeholder: numberText('1.00'), number: true }),
        /* Which account it was taken on: one's own money, or a prop account -- whose rules then count it. */
        !backtest && (state.prop || []).length > 0 && h('div', { key: 'acct', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
          label(s.jAccount),
          select(inputs, 'tlAccount', [{ value: '', label: s.jOwnAccount }, ...(state.prop || []).map(acc => ({
            value: acc.id, label: [acc.firm, acc.program].filter(Boolean).join(' ')
          }))], { ...frame, padding: '0 10px', fontSize: '13px', width: '100%' }, {
            value: d.propAccountId || '', ariaLabel: s.jAccount,
            onChange: value => {
              const acc = (state.prop || []).find(row => row.id === value);
              setD({ propAccountId: value || null, ...(acc ? { currency: acc.currency } : {}) });
            }
          })),
        h('div', { key: 'pl', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0, gridColumn: mobile ? '1 / -1' : 'span 2' }) },
          label(s.tlPlL),
          h('div', { style: css({ ...frame, display: 'flex', alignItems: 'center', gap: '6px', padding: '0 4px 0 12px' }) },
            input(inputs, 'tlPl', s.jPlHint, {
              type: 'number', value: editing ? money(editing.netPl) : undefined, onEnter: save, ariaLabel: s.tlPlL,
              style: { flex: 1, minWidth: 0, background: 'transparent', border: 'none', padding: 0, fontFamily: MONO, fontSize: '14px', fontWeight: 700 }
            }),
            seg('currency', quick.map(code => [code, code])),
            select(inputs, 'tlCurOther', [{ value: '', label: '…' }, ...others.map(code => ({ value: code, label: code }))],
              { width: '52px', height: '36px', padding: '0 4px', border: 'none', background: 'transparent', fontFamily: MONO, fontSize: '11.5px', color: C.muted },
              { value: '', ariaLabel: s.a11yOtherCurrency, onChange: code => code && setD({ currency: code }) })))),
      recent.length > 0 && h('div', { key: 'recent', style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }) },
        h('span', { style: css({ fontSize: '11.5px', color: C.faint }) }, s.tlRecent),
        ...recent.map(pair => h('div', {
          key: 'r' + pair, ...tap(() => { const node = inputs.node('tlPair'); if (node) node.value = pair; }, pair), class: 'pt-button',
          style: css({ height: '30px', display: 'flex', alignItems: 'center', padding: '0 10px', borderRadius: '7px', border: `1px solid ${C.stroke}`, fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, color: C.textDim, cursor: 'pointer' })
        }, pair)))),

    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '10px' }) },
      step(2, s.tlStep2, s.tlOptional),
      grid(260,
        field('tlSession', s.tlSessionL, editing ? editing.session : undefined, { placeholder: s.tlSessionPh }),
        h('div', { key: 'tf', style: css({ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }) },
          label(s.tlTfL),
          seg('tf', tfs.map(tf => [tf, tf]), { clear: true }))),
      grid(260,
        field('tlSetup', s.tlSetupL, editing ? editing.setup : undefined, { placeholder: s.tlSetupPh }),
        field('tlReport', s.tlReportL, editing ? editing.reportUrl : undefined, { placeholder: s.jReportHint }))),

    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '10px' }) },
      step(3, s.tlStep3),
      h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: '8px' }) },
        question(s.tlQExit, seg('exit', TRADE_EXITS.map(exit => [exit, s.tlExits[exit] || exit]), { clear: true })),
        question(s.tlQRisk, seg('risk', TRADE_RISKS.map(risk => [risk, s.tlRisks[risk] || risk]), { clear: true })),
        question(s.tlQRules, seg('rules', [[true, s.jYesShort], [false, s.jNoShort]], { colors: { true: '#1F4A3D', false: '#5A2F33' } })),
        question(s.tlQMm, seg('mm', [[true, s.jYesShort], [false, s.jNoShort]], { colors: { true: '#1F4A3D', false: '#5A2F33' } })))),

    limit !== null && h('div', { key: 'plan' }, planLine(spent ? s.planJournalSpent : s.planJournalUsed(used, limit), { spent })),
    h('div', { style: css({ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', paddingTop: '4px', borderTop: `1px solid ${C.line}` }) },
      h('span', { style: css({ flex: '1 1 220px', fontSize: '12.5px', color: verdict[1] }) }, verdict[0]),
      h('div', { ...tap(close, s.cfCancel), style: css({ height: '44px', display: 'flex', alignItems: 'center', padding: '0 14px', color: C.muted, fontSize: '13px', cursor: 'pointer' }) }, s.cfCancel),
      h('div', { style: css({ opacity: spent ? 0.45 : 1 }) }, pill(editing ? s.cfSaveChanges : s.jAdd, save, { primary: true, key: 'save' })))
  );
}

// ------------------------------------------------------------ calendar ---

/**
 * Days, weeks or months of results, shaded by size. A period with trades is a
 * filter: clicking it narrows the list to its dates, clicking it again lets go.
 */
export function pnlCalendar(trades, { state, t, fmt, actions, mobile }) {
  const s = t.shell;
  const ui = uiOf(state);
  const m = journalMoney(trades, state);
  const latest = trades.map(row => row.date).sort().pop() || today();
  const anchor = ui.calMonth || latest.slice(0, 7);
  const [year, month] = anchor.split('-').map(Number);
  const mode = ui.calMode || 'day';
  const range = ui.range;
  const sumOf = list => list.reduce((total, row) => total + (m.value(row) || 0), 0);
  const short = value => {
    const a = Math.abs(value);
    return (value > 0.005 ? '+' : value < -0.005 ? '−' : '') + (a >= 1000 ? fmt.number(a / 1000, 1) + 'K' : fmt.number(Math.round(a), 0));
  };
  const money = value => fmt.signedAmount(value, m.currency);
  const picked = (from, to) => Boolean(range) && range.from === from && range.to === to;
  const pick = (from, to, text) => actions.setJournalUi({ range: picked(from, to) ? null : { from, to, label: text }, openId: null });
  const heat = (value, max) => {
    if (Math.abs(value) < 0.005) return [C.line, C.strokeHi, C.textDim];
    const strong = Math.abs(value) >= max * 0.5;
    return value > 0 ? (strong ? HEAT.upStrong : HEAT.up) : (strong ? HEAT.downStrong : HEAT.down);
  };
  const byDay = {};
  trades.forEach(row => { (byDay[row.date] = byDay[row.date] || []).push(row); });
  const nowDay = today();

  const step = by => {
    if (mode === 'month') return actions.setJournalUi({ calMonth: (year + by) + '-' + pad(month) });
    const next = new Date(year, month - 1 + by, 1);
    actions.setJournalUi({ calMonth: next.getFullYear() + '-' + pad(next.getMonth() + 1) });
  };
  const inPeriod = date => (mode === 'month' ? date.slice(0, 4) === String(year) : date.slice(0, 7) === anchor);
  const periodTrades = trades.filter(row => inPeriod(row.date));
  const days = Object.keys(byDay).filter(inPeriod);
  const up = days.filter(day => sumOf(byDay[day]) > 0.005).length;
  const down = days.filter(day => sumOf(byDay[day]) < -0.005).length;
  const total = sumOf(periodTrades);
  const monthName = index => t.months[index];

  // Weeks of the month on screen, Monday first, with a total column.
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  const cursor = new Date(year, month - 1, 1 - ((first.getDay() + 6) % 7));
  const weeks = [];
  while (cursor <= last) {
    const list = [];
    for (let i = 0; i < 7; i++) { list.push(new Date(cursor)); cursor.setDate(cursor.getDate() + 1); }
    weeks.push(list);
  }
  const dayMax = Math.max(1, ...Object.keys(byDay).filter(day => day.slice(0, 7) === anchor).map(day => Math.abs(sumOf(byDay[day]))));
  const weekRows = weeks.map(list => {
    const inside = list.filter(day => day.getMonth() === month - 1);
    const rows = inside.flatMap(day => byDay[ymd(day)] || []);
    return { list, inside, rows, value: sumOf(rows), from: ymd(list[0]), to: ymd(list[6]), number: isoWeek(list[0]) };
  });

  const cell = (key, top, value, colors, { selected = false, today: isToday = false, out = false, onClick = null, titleText = '' } = {}) => h('div', {
    key, ...(onClick ? tap(onClick, titleText) : {}), title: titleText || null,
    style: css({
      minHeight: mobile ? '44px' : '52px', minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      padding: mobile ? '4px' : '5px 6px', borderRadius: '7px', cursor: onClick ? 'pointer' : 'default',
      background: out ? 'transparent' : colors ? colors[0] : C.panel,
      border: `1px solid ${selected ? C.text : isToday ? C.accent : colors ? colors[1] : out ? 'transparent' : C.line}`
    })
  },
    mono(top, { fontSize: mobile ? '9.5px' : '10.5px', color: out ? C.ghost : isToday ? C.accent : C.muted }),
    mono(value, { fontSize: mobile ? '9.5px' : '11px', fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap', color: colors ? colors[2] : C.faint }));

  let body;
  if (mode === 'day') {
    const cells = [];
    weekRows.forEach(week => {
      week.list.forEach(day => {
        const key = ymd(day);
        const out = day.getMonth() !== month - 1;
        const list = out ? [] : (byDay[key] || []);
        const value = sumOf(list);
        const colors = list.length ? heat(value, dayMax) : null;
        const text = day.getDate() + ' ' + monthName(day.getMonth());
        cells.push(cell(key, String(day.getDate()), list.length ? short(value) : '', colors, {
          out, today: key === nowDay, selected: picked(key, key),
          onClick: list.length ? () => pick(key, key, text) : null,
          titleText: list.length ? text + ': ' + money(value) + ' · ' + s.tlTrShort(list.length) : ''
        }));
      });
      cells.push(cell('w' + week.from, 'Σ', week.rows.length ? short(week.value) : '', week.rows.length ? [ 'transparent', C.line, tone(week.value)] : null, {
        selected: picked(week.from, week.to), onClick: week.rows.length ? () => pick(week.from, week.to, s.tlWeekShort(week.number)) : null,
        titleText: week.rows.length ? s.tlWeek(week.number) + ': ' + money(week.value) : ''
      }));
    });
    body = h('div', { key: 'days', style: css({ display: 'flex', flexDirection: 'column', gap: '4px' }) },
      h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr)) minmax(0,1.2fr)', gap: mobile ? '3px' : '4px' }) },
        ...[...s.tlDow, 'Σ'].map((text, i) => mono(text, { key: 'h' + i, textAlign: 'center', fontSize: '10px', letterSpacing: '1px', color: i === 5 || i === 6 || i === 7 ? C.faint : C.muted }))),
      h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr)) minmax(0,1.2fr)', gap: mobile ? '3px' : '4px' }) }, ...cells));
  } else if (mode === 'week') {
    const max = Math.max(1, ...weekRows.map(week => Math.abs(week.value)));
    body = h('div', { key: 'weeks', style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.ghost}` }) },
      ...weekRows.map(week => {
        const has = week.rows.length > 0;
        return h('div', {
          key: week.from, ...(has ? tap(() => pick(week.from, week.to, s.tlWeekShort(week.number)), s.tlWeek(week.number)) : {}),
          style: css({
            display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(50px,1fr) auto', gap: '12px', alignItems: 'center', minHeight: '54px', padding: '6px 4px',
            borderBottom: `1px solid ${C.line}`, background: picked(week.from, week.to) ? C.line : 'transparent', cursor: has ? 'pointer' : 'default'
          })
        },
          h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
            h('span', { style: css({ fontSize: '13px', fontWeight: 600 }) }, s.tlWeek(week.number)),
            h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' }) },
              week.inside[0].getDate() + '–' + week.inside[week.inside.length - 1].getDate() + ' ' + monthName(month - 1) + ' · ' + s.tlTrShort(week.rows.length))),
          h('div', { style: css({ height: '6px', borderRadius: '3px', background: C.stroke, overflow: 'hidden' }) },
            h('div', { style: css({ width: pct(Math.abs(week.value) / max), height: '100%', borderRadius: '3px', background: week.value >= 0 ? C.accent : DANGER_TEXT }) })),
          mono(has ? money(week.value) : '—', { minWidth: '64px', textAlign: 'right', fontSize: '13.5px', fontWeight: 700, color: has ? tone(week.value) : C.faint, whiteSpace: 'nowrap' }));
      }));
  } else {
    const months = Array.from({ length: 12 }, (_, i) => {
      const key = year + '-' + pad(i + 1);
      const rows = trades.filter(row => row.date.slice(0, 7) === key);
      return { i, key, rows, value: sumOf(rows) };
    });
    const max = Math.max(1, ...months.map(item => Math.abs(item.value)));
    body = h('div', { key: 'months', style: css({ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '6px' }) },
      ...months.map(item => {
        const has = item.rows.length > 0;
        const colors = has ? heat(item.value, max) : null;
        const from = item.key + '-01';
        const to = item.key + '-31';
        const now = item.key === nowDay.slice(0, 7);
        return h('div', {
          key: item.key, ...(has ? tap(() => pick(from, to, monthName(item.i) + ' ' + year), monthName(item.i)) : {}),
          style: css({
            minHeight: '66px', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '4px', padding: '8px 10px', borderRadius: '9px',
            background: colors ? colors[0] : C.panel, border: `1px solid ${picked(from, to) ? C.text : now ? C.accent : colors ? colors[1] : C.line}`, cursor: has ? 'pointer' : 'default'
          })
        },
          mono(monthName(item.i).toUpperCase(), { fontSize: '10.5px', letterSpacing: '1px', color: now ? C.accent : C.muted }),
          mono(has ? money(item.value) : '—', { fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', color: colors ? colors[2] : C.faint }),
          h('span', { style: css({ fontSize: '10.5px', color: C.muted }) }, has ? s.tlTrShort(item.rows.length) : ''));
      }));
  }

  const modeButton = ([key, text]) => {
    const on = mode === key;
    return h('div', {
      key, ...tap(() => actions.setJournalUi({ calMode: key }), text), role: 'radio', 'aria-checked': on ? 'true' : 'false',
      style: css({
        height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '7px', cursor: 'pointer',
        background: on ? C.strokeHi : 'transparent', fontSize: '12.5px', fontWeight: 600, color: on ? C.text : C.muted
      })
    }, text);
  };
  const arrow = (glyph, by) => h('div', {
    ...tap(() => step(by), glyph), class: 'pt-button',
    style: css({ width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '7px', color: C.textDim, fontSize: '16px', cursor: 'pointer' })
  }, glyph);

  return box(mobile, { gap: '14px' },
    h('div', { style: css({ display: 'flex', alignItems: 'flex-start', gap: '10px' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }) },
        h('div', { style: css({ fontSize: mobile ? '14px' : '15px', fontWeight: 700 }) }, s.tlCal),
        h('div', { style: css({ fontSize: '12.5px', color: C.muted, lineHeight: 1.4 }) },
          s.tlCalSub(periodTrades.length, up, down) + (m.mixed ? ' · ' + s.tlCalMixed(m.base) : ''))),
      h('div', { style: css({ flex: 1 }) }),
      mono(money(total), { fontSize: '14px', fontWeight: 700, color: tone(total), whiteSpace: 'nowrap' })),
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }) },
      h('div', { role: 'radiogroup', style: css({ flex: '1 1 210px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '3px', padding: '3px', borderRadius: '10px', background: C.panel, border: `1px solid ${C.stroke}` }) },
        ...[['day', s.tlModes[0]], ['week', s.tlModes[1]], ['month', s.tlModes[2]]].map(modeButton)),
      h('div', { style: css({ flex: '1 1 180px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px', padding: '3px', borderRadius: '10px', border: `1px solid ${C.stroke}` }) },
        arrow('‹', -1),
        mono(mode === 'month' ? String(year) : (s.tlMonthsFull[month - 1] || '').toUpperCase() + ' ' + year, { fontSize: '12px', letterSpacing: '1px', whiteSpace: 'nowrap' }),
        arrow('›', 1))),
    body,
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 14px', fontSize: '11.5px', color: C.muted }) },
      h('span', { style: css({ display: 'flex', alignItems: 'center', gap: '6px' }) }, h('span', { style: css({ width: '10px', height: '10px', borderRadius: '3px', background: HEAT.upStrong[0] }) }), s.tlGain),
      h('span', { style: css({ display: 'flex', alignItems: 'center', gap: '6px' }) }, h('span', { style: css({ width: '10px', height: '10px', borderRadius: '3px', background: HEAT.downStrong[0] }) }), s.tlLoss),
      h('span', null, s.tlCalHint))
  );
}
